import { HostingEnvironment } from '@opengov/psp-contract';
import type { EntityBase, FederatedServiceIds } from '@opengov/psp-contract';
import { AppConfig, EntitlementProduct, EntityApplicationProductKey, MenuItem } from '../types';

/**
 * Resolves the tenant slug a suite product uses in its own hostnames and paths.
 *
 * A product's tenant is provisioned independently of the PSP entity
 * `friendlyId`, so the two frequently diverge, and an entity's slug can differ
 * per product — one may carry a state suffix the product's tenant drops, while
 * another is an unrelated abbreviation. Deriving a product host from
 * `friendlyId` therefore sends the user to a hostname that was never
 * provisioned, which typically surfaces as a bare nginx 404 inside the shell.
 *
 * Control Panel publishes the authoritative per-product value on the entity's
 * `federatedServiceIds`, so prefer that. Fall back to `friendlyId` when the id
 * is absent (Control Panel returns `null` for products an entity has not been
 * federated into) so entities whose slugs do happen to match keep working —
 * this is never worse than deriving from `friendlyId` alone.
 *
 * @param entity - The active entity, if one is selected
 * @param key - The `federatedServiceIds` key owned by the calling product
 * @returns The product's tenant slug, or `''` when neither value is available
 */
export const resolveProductTenantSlug = (
  entity: EntityBase | undefined,
  key: keyof FederatedServiceIds
): string => entity?.federatedServiceIds?.[key] || entity?.friendlyId || '';

const COMMUNITY_UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// One DNS label: the only shape that is valid as `{slug}.workflow.opengov.com`. A value with
// `/`, `@`, `:`, `?`, `#` or a dot would otherwise change the host. Mirrored by communityFromEntity
// in plc-ember-apps (packages/cit-components/src/setup/psp/pspEnv.ts); keep the two in step.
const COMMUNITY_SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i;

/** The trimmed citCommunityId when it is a usable slug; undefined for anything else. */
function usablePlcCommunitySlug(value: string | null | undefined): string | undefined {
  if (typeof value !== 'string') return undefined;
  const slug = value.trim();
  if (!COMMUNITY_SLUG_PATTERN.test(slug)) return undefined;
  // A UUID also matches the label pattern, but it is not a subdomain.
  return COMMUNITY_UUID_PATTERN.test(slug) ? undefined : slug;
}

/**
 * The PLC workflow subdomain. Prefer `citCommunityId` when it is a usable slug; the entity
 * `friendlyId` is a different name whenever PLC was provisioned separately, and a host built
 * from it was never registered.
 */
export function plcCommunitySlug(entity: EntityBase | undefined): string {
  const citCommunityId = usablePlcCommunitySlug(entity?.federatedServiceIds?.citCommunityId);
  if (citCommunityId) return citCommunityId;
  // Same guard on the fallback: an unusable friendlyId gives no host, not a host-changing one.
  return usablePlcCommunitySlug(entity?.friendlyId) ?? '';
}

const VALID_HOSTING_ENVS: ReadonlySet<string> = new Set<string>(Object.values(HostingEnvironment));

function coerceHostingEnv(value: unknown): HostingEnvironment | null {
  return typeof value === 'string' && VALID_HOSTING_ENVS.has(value)
    ? (value as HostingEnvironment)
    : null;
}

let pspEnvLogShown = false;

/**
 * Resolves the PSP hosting environment from the build-time-stamped
 * <meta name="psp-env"> tag in index.html. The %MODE% placeholder is
 * substituted by Vite's native HTML env replacement during `vite build`
 * and `vite serve`, driven by the resolved `--mode` flag.
 *
 * If the tag is missing or contains an invalid value (build pipeline broke,
 * a non-PSP HTML got served, or the meta tag was removed), logs an error
 * once per session and falls back to DEVELOPMENT.
 */
export function detectPspHostingEnvironment(): HostingEnvironment {
  const metaContent =
    typeof document !== 'undefined'
      ? document.querySelector('meta[name="psp-env"]')?.getAttribute('content')
      : null;

  const env = coerceHostingEnv(metaContent);
  if (env) return env;

  if (!pspEnvLogShown) {
    pspEnvLogShown = true;
    console.error(
      `[PSP] <meta name="psp-env"> missing or invalid (got "${metaContent ?? 'null'}"). ` +
        `Falling back to ${HostingEnvironment.DEVELOPMENT}. ` +
        `Check that index.html contains <meta name="psp-env" content="%MODE%"> ` +
        `and that the build ran with an explicit --mode flag.`
    );
  }
  return HostingEnvironment.DEVELOPMENT;
}

/** Test-only: resets the one-time error log flag so each test can observe it fresh. */
export function _resetPspEnvLogStateForTests(): void {
  pspEnvLogShown = false;
}

/**
 * Checks if the current hosting environment is Development or Integration.
 * Use this to conditionally enable features that should only be available
 * in lower environments (e.g., module loading vs external URLs).
 */
export function isDevOrIntegrationEnv(): boolean {
  const env = detectPspHostingEnvironment();
  return env === HostingEnvironment.DEVELOPMENT || env === HostingEnvironment.INTEGRATION;
}

/**
 * Secret override for showing PSP dev tools in non-development environment.
 * Checks sessionStorage for the 'showSecretPspDevTools' flag.
 *
 * @returns True if the secret override is enabled in sessionStorage, false otherwise
 * @todo Remove this in future stable versions of the PSP.
 */
export function shouldShowSecretPspDevTools(): boolean {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return false;
  }
  return window.sessionStorage.getItem('showSecretPspDevTools') === 'true';
}

/**
 * Checks if the Data Creator module should be shown in the Integration environment.
 * Reads the localStorage flag 'psp.showDataCreatorInIntegration'.
 *
 * @returns True if the flag is set to 'true' in localStorage, false otherwise
 */
export function shouldShowDataCreatorInIntegration(): boolean {
  if (typeof window === 'undefined' || !window.localStorage) return false;
  return window.localStorage.getItem('psp.showDataCreatorInIntegration') === 'true';
}

/**
 * Toggles the Data Creator visibility flag in localStorage and logs the result.
 * Flips the state of 'psp.showDataCreatorInIntegration' and logs whether it's now enabled or disabled.
 */
export function toggleDataCreatorVisibility(): void {
  const next = !shouldShowDataCreatorInIntegration();
  window.localStorage.setItem('psp.showDataCreatorInIntegration', String(next));
  console.log(`[PSP] Data Creator visibility: ${next ? 'enabled' : 'disabled'}. Refresh to apply.`);
}

/**
 * Product keys recognized by `resolveDevModuleUrl`. Each key maps to the env
 * var `VITE_DEV_MODULE_URL_<KEY>` (settable in `.env.development.local`) that
 * lets an engineer point PSP at their local dev server for that app. Add a
 * new entry here when migrating a new product to the dev-override pattern,
 * and keep `.env.example` in sync with the same set.
 */
export type DevModuleProductKey =
  | 'APP_BUILDER'
  | 'DATA_MANAGER'
  | 'ASSET_MANAGEMENT'
  | 'BANK_RECONCILIATION'
  | 'BUDGET'
  | 'COMMAND_CENTER'
  | 'COMMUNITY_FEEDBACK'
  | 'INVENTORY'
  | 'FIXED_ASSETS'
  | 'GENERAL_LEDGER'
  | 'LEGIS'
  | 'OG360'
  | 'PERFORMANCE_MANAGEMENT'
  | 'PLC_EMPLOYEE'
  | 'PROCUREMENT'
  | 'REPORTING'
  | 'VENDOR_MANAGEMENT'
  | 'CLM'
  | 'WORKFLOW_BUILDER'
  | 'STORIES'
  | 'ATLAS'
  | 'PCARD'
  | 'SITES'
  | 'ORG_REGISTRY'
  | 'ONBOARDING'
  | 'ACCESS_MANAGEMENT';

/**
 * Resolves the module URL to use for an app's `DEVELOPMENT` hosting case.
 *
 * Engineers actively working on a remote app set `VITE_DEV_MODULE_URL_<KEY>`
 * in `.env.development.local` (e.g. `VITE_DEV_MODULE_URL_BUDGET=http://localhost:10003/psp/budget-psp.js`)
 * to point PSP at their local dev server. Everyone else gets the integration
 * URL by default, so unrelated apps don't break with stale localhost links.
 *
 * Optional override — empty/missing values fall through to `integrationUrl`.
 */
export function resolveDevModuleUrl(
  productKey: DevModuleProductKey,
  integrationUrl: string
): string {
  const envKey = `VITE_DEV_MODULE_URL_${productKey}` as const;
  const override = typeof import.meta.env !== 'undefined' ? import.meta.env[envKey] : undefined;
  return typeof override === 'string' && override.length > 0 ? override : integrationUrl;
}

//TODO: temporary workaround to match the absolute API URL with the corresponding hosting environment domain
export const getHostingEnvironmentDomain = (): string => {
  const detectedHostingEnvironment = detectPspHostingEnvironment();
  switch (detectedHostingEnvironment) {
    case HostingEnvironment.DEVELOPMENT:
      return 'ogintegration.us';
    case HostingEnvironment.INTEGRATION:
      return 'ogintegration.us';
    case HostingEnvironment.STAGING:
      return 'ogstaging.us';
    case HostingEnvironment.PRODUCTION:
      return 'opengov.com';
    default:
      return 'ogintegration.us';
  }
};

/**
 * Bare PSP / Platform hostname for the given hosting domain.
 * Production uses the vanity host `app.opengov.com`; other envs use `psp.{domain}`.
 */
export const getPspHost = (domain: string = getHostingEnvironmentDomain()): string =>
  domain === 'opengov.com' ? 'app.opengov.com' : `psp.${domain}`;

/** HTTPS origin for the PSP / Platform host (`https://` + `getPspHost()`). */
export const getPspOrigin = (): string => `https://${getPspHost()}`;

/**
 * Resolves a URL for ES modules hosted on the PSP origin under `/remote-modules/`.
 * Local dev serves the shell from `psp.ogov.me` while modules live on the hosted
 * PSP bucket — use a same-origin path in development so Vite's `/remote-modules`
 * proxy can forward the request and dynamic `import()` avoids CORS.
 */
export function resolvePspHostedRemoteModuleUrl(modulePath: string): string {
  const path = modulePath.startsWith('/') ? modulePath : `/${modulePath}`;

  if (
    detectPspHostingEnvironment() === HostingEnvironment.DEVELOPMENT &&
    typeof window !== 'undefined'
  ) {
    return `${window.location.origin}${path}`;
  }

  return `${getPspOrigin()}${path}`;
}

/**
 * Calculates a specificity score for a route pattern.
 * Higher scores indicate more specific/restrictive patterns.
 *
 * Scoring factors:
 * - More path segments = more specific
 * - More literal segments (non-parameters) = more specific
 * - Fewer wildcards/parameters = more specific
 *
 * @param pattern - The route pattern string (e.g., "/reports/:id/details")
 * @returns A numeric score where higher = more specific
 */
export function calculateRouteSpecificity(pattern: string): number {
  if (pattern === '/') return 0;

  const segments = pattern.split('/').filter((seg) => seg.length > 0);
  let score = 0;
  let literalCount = 0;
  let paramCount = 0;

  for (const segment of segments) {
    if (segment.startsWith(':') || segment === '*' || segment.includes('*')) {
      paramCount++;
      score += 1;
    } else {
      literalCount++;
      score += 10;
    }
  }

  score += segments.length * 5;

  if (paramCount > 0 && literalCount === 0) {
    score -= paramCount * 2;
  }

  return score;
}

/**
 * Checks if an app is a remote app (has moduleRoutes and no component).
 * Remote apps are loaded from external module URLs via dynamic route resolution,
 * while local apps have React components.
 *
 */
export function isRemoteApp(app: AppConfig): boolean {
  return (
    'moduleRoutes' in app &&
    app.moduleRoutes !== undefined &&
    !('component' in app && app.component)
  );
}

/**
 * Type guard to check if an app is an iframe app (has externalUrl but not openInNewTab).
 * Iframe apps are rendered in an iframe within the PSP shell.
 *
 * @param app - The app configuration to check
 * @returns True if the app is an iframe app, false otherwise
 */
export function isIFrameApp(app: AppConfig | MenuItem): app is AppConfig & { externalUrl: string } {
  // Treat only a non-empty externalUrl as "external": a config can resolve
  // externalUrl to '' to opt out (e.g. PLC when it mounts in-shell — see appRegistry).
  const hasExternalUrl = 'externalUrl' in app && !!app.externalUrl;
  const hasOpenInNewTab = 'openInNewTab' in app && app.openInNewTab === true;
  return hasExternalUrl && !hasOpenInNewTab;
}

export function isNewTabApp(app: AppConfig | MenuItem): app is AppConfig & { externalUrl: string } {
  // See isIFrameApp: an empty externalUrl means "not external" (PLC in-shell mount).
  const hasExternalUrl = 'externalUrl' in app && !!app.externalUrl;
  const hasOpenInNewTab = 'openInNewTab' in app && app.openInNewTab === true;
  return hasExternalUrl && hasOpenInNewTab;
}

/**
 * Reference data for possible entitlement application responses from the entity entitlement API.
 *
 * Used to link appRegistry menu items with user entitlements. The API call in EntityContext.tsx
 * returns which applications a user can access. This array maps those entitlements to menu items,
 * ensuring users only see menu options for applications they're entitled to use.
 *
 * @documentation This is documentation-only reference data, not used in runtime code.
 * Referenced in comments in appRegistry.ts for developer reference.
 */
export const ENTITLED_ENTITY_APPLICATIONS_RESPONSE: EntitlementProduct[] = [
  {
    entitlementProductKey: EntityApplicationProductKey.ADMIN_SETTINGS,
    url: 'https://controlpanel.ogintegration.us/entity/kp0uh6zcwcqgco7/settings',
    description: 'Centrally Manage your Entity data including Groups, Users and Roles',
    displayName: 'Admin Settings',
  },
  {
    entitlementProductKey: EntityApplicationProductKey.UTILITY_BILLING,
    url: 'https://ubwm.ub.opengov.engineering/admin',
    description: 'Utility Billing',
    displayName: 'Utility Billing',
  },
  {
    entitlementProductKey: EntityApplicationProductKey.BUDGET,
    url: 'https://controlpanel.ogintegration.us/budgets/zuberentity',
    description: 'Year-round budget management',
    displayName: 'Budgeting & Performance',
  },
  {
    entitlementProductKey: EntityApplicationProductKey.PERFORMANCE_MANAGEMENT,
    url: 'https://controlpanel.ogintegration.us/budgeting/zuberentity/performance',
    description: 'Align and track performance goals',
    displayName: 'Performance Management',
  },
  {
    entitlementProductKey: EntityApplicationProductKey.REPORTING,
    url: 'https://controlpanel.ogintegration.us/reporting/zuberentity',
    description: 'Analyze and share data and insights',
    displayName: 'Reporting',
  },
  {
    entitlementProductKey: EntityApplicationProductKey.AGENT_STUDIO,
    url: 'https://ai.ogintegration.us/entity/controlpanel',
    description: 'Agent Studio',
    displayName: 'Agent Studio',
  },
  {
    entitlementProductKey: EntityApplicationProductKey.APP_BUILDER,
    url: 'https://apps.ogintegration.us/controlpanel',
    description: 'App Builder',
    displayName: 'App Builder',
  },
  {
    entitlementProductKey: EntityApplicationProductKey.COMMUNITY_FEEDBACK,
    url: 'https://communityfeedback.ogintegration.us/portals/15oth/forum_home',
    description: 'Collect feedback from surveys and meetings',
    displayName: 'Community Feedback',
  },
  {
    entitlementProductKey: EntityApplicationProductKey.DEVELOPER_PORTAL,
    url: 'https://developer.vpctest.com',
    description: 'Developer Portal',
    displayName: 'Developer Portal',
  },
  {
    entitlementProductKey: EntityApplicationProductKey.TAX_REVENUE,
    url: 'https://abcd.taxandrevenue.opengov.engineering',
    description: 'Tax & Revenue',
    displayName: 'Tax & Revenue',
  },
  {
    entitlementProductKey: EntityApplicationProductKey.FINANCIALS,
    url: '',
    description: 'Placeholder for Financials app.',
    displayName: 'Financials (Placeholder)',
  },
  {
    entitlementProductKey: EntityApplicationProductKey.PROCUREMENT,
    url: '',
    description: 'Placeholder for Procurement app.',
    displayName: 'Procurement (Placeholder)',
  },
  {
    entitlementProductKey: EntityApplicationProductKey.VENDOR_MANAGEMENT,
    url: 'https://vendor.procurement.ogintegration.us/psp/vendor-management.js',
    description: 'Vendor management application',
    displayName: 'Vendor Management',
  },
  {
    entitlementProductKey: EntityApplicationProductKey.CLM,
    url: 'https://contract-management.procurement.ogintegration.us/psp/index.js',
    description: 'Contract lifecycle management application',
    displayName: 'Contract Lifecycle Management',
  },
  {
    entitlementProductKey: EntityApplicationProductKey.PERMITTING_LICENSING,
    url: '',
    description: 'Placeholder for Permitting & Licensing/Workflow Management app.',
    displayName: 'Permitting & Licensing / Workflow (Placeholder)',
  },
  {
    entitlementProductKey: EntityApplicationProductKey.ASSET_MANAGEMENT,
    url: '',
    description: 'Placeholder for Asset Management app.',
    displayName: 'Asset Management (Placeholder)',
  },
  {
    entitlementProductKey: EntityApplicationProductKey.SITES,
    url: 'https://mfe.blueprint.ogintegration.us/blueprint-ui/0.0.1/index.js',
    description: 'Blueprint CMS for government websites',
    displayName: 'Sites',
  },
];
